import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailQuestion, Phone, Wrench } from "lucide-react";
import { PublicAPI, buildSignedUrl } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const STEP_ORDER = ["booked", "in_progress", "awaiting_approval", "awaiting_parts", "qc", "ready", "delivered"];
const STEP_LABEL = {
    booked: "Booked", in_progress: "In progress", awaiting_approval: "Approval",
    awaiting_parts: "Parts", qc: "QC", ready: "Ready", delivered: "Delivered",
};

export default function PublicJobStatus() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "AutoBrix · Your service";
        PublicAPI.job(token)
            .then(setData)
            .catch((e) => setErr(e?.response?.data?.error?.message || "We could not find your job."))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <div className="grid min-h-screen place-items-center bg-black text-white">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }
    if (err) {
        return (
            <div className="grid min-h-screen place-items-center bg-black p-6 text-white" data-testid="public-not-found">
                <div className="max-w-md text-center">
                    <div className="font-mono text-xs uppercase tracking-widest text-brand-red">AutoBrix</div>
                    <h1 className="mt-4 font-display text-3xl font-bold">We couldn't find this booking.</h1>
                    <p className="mt-2 text-sm text-zinc-400">Please double-check the link your workshop shared with you.</p>
                </div>
            </div>
        );
    }

    const status = data.status;
    const isCancelled = status === "cancelled";
    const currentIdx = STEP_ORDER.indexOf(status);

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50" data-testid="public-job-status">
            {/* Banner */}
            <div className="bg-black px-5 py-4 text-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <span className="font-display text-lg font-extrabold tracking-tight">AutoBrix</span>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{data.workshop?.name}</span>
                    </div>
                    {data.workshop?.phone && (
                        <a href={`tel:${data.workshop.phone}`} className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1 text-xs hover:bg-white/10" data-testid="public-call">
                            <Phone className="h-3.5 w-3.5" /> {data.workshop.phone}
                        </a>
                    )}
                </div>
            </div>

            <main className="mx-auto max-w-3xl px-5 py-8 sm:py-12">
                {/* Hero */}
                <div className="mb-8">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {data.workshop?.branch_name} · {data.job_number}
                    </div>
                    <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                        Hello <span data-testid="public-customer-name">{data.customer_name_first_only || "there"}</span> — here's your service.
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        <span className="font-mono" data-testid="public-vehicle-reg">{data.vehicle?.registration_number_masked}</span> · {data.vehicle?.make} {data.vehicle?.model} {data.vehicle?.year}
                    </p>
                </div>

                {/* Status card */}
                <section className={`rounded-xl border p-5 ${isCancelled ? "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40" : "border-brand-redSoft bg-white dark:border-brand-red/30 dark:bg-zinc-900"}`}>
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
                        <Wrench className="h-3.5 w-3.5 text-brand-red" />
                        <span className="text-brand-redDeep dark:text-brand-red">Current status</span>
                    </div>
                    <div className="mt-2 font-display text-2xl font-bold" data-testid="public-status-pill">{data.status_label}</div>
                    <div className="mt-1 text-sm text-zinc-500">{formatDateTime(data.opened_at)} → now</div>

                    {!isCancelled && (
                        <div className="mt-5 space-y-3">
                            <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                                <div className="h-full bg-brand-red transition-[width] duration-500" style={{ width: `${data.progress_percent}%` }} />
                            </div>
                            <ol className="grid grid-cols-7 gap-0.5">
                                {STEP_ORDER.map((s, i) => {
                                    const reached = i <= currentIdx;
                                    return (
                                        <li key={s} className={`flex flex-col items-center text-center text-[10px] ${reached ? "text-brand-redDeep dark:text-brand-red" : "text-zinc-400"}`}>
                                            <span className={`mb-1 grid h-5 w-5 place-items-center rounded-full ${reached ? "bg-brand-red text-white" : "bg-zinc-200 text-zinc-500 dark:bg-zinc-800"}`}>
                                                {reached ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                                            </span>
                                            <span className="hidden sm:block">{STEP_LABEL[s]}</span>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </section>

                {/* Photos */}
                {data.attachments_public?.length > 0 && (
                    <section className="mt-6">
                        <h2 className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Photos from the workshop</h2>
                        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {data.attachments_public.map((a, i) => (
                                <a key={i} href={buildSignedUrl(a.thumb_signed_url)} target="_blank" rel="noreferrer"
                                   className="overflow-hidden rounded-md border bg-white dark:bg-zinc-900" data-testid={`public-photo-${i}`}>
                                    <img src={buildSignedUrl(a.thumb_signed_url)} alt={a.caption || a.kind} loading="lazy" className="aspect-square w-full object-cover" />
                                    <div className="p-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-500">{a.kind}</div>
                                </a>
                            ))}
                        </div>
                    </section>
                )}

                {/* Help */}
                <section className="mt-8 flex flex-col items-start gap-3 rounded-md border bg-white p-4 dark:bg-zinc-900 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="flex items-center gap-1.5 text-sm font-medium">
                            <MailQuestion className="h-4 w-4 text-brand-red" /> Need help?
                        </div>
                        <p className="mt-1 text-xs text-zinc-500">Reach out to the workshop — your service advisor will respond.</p>
                    </div>
                    <div className="flex gap-2">
                        {data.workshop?.phone && (
                            <a href={`tel:${data.workshop.phone}`} className="rounded-md border bg-brand-red px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-redDeep">
                                Call
                            </a>
                        )}
                        {data.workshop?.email && (
                            <a href={`mailto:${data.workshop.email}`} className="rounded-md border px-3 py-1.5 text-xs font-medium">
                                Email
                            </a>
                        )}
                    </div>
                </section>

                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                    Powered by AutoBrix · This link is private to you
                </p>
            </main>
        </div>
    );
}
